import { PrivateAPINamespace } from '../PrivateAPINamespace';
import type {
  IPage,
  AddTemplateOptions,
  AddExtensionOptions,
} from '@wix/public-editor-platform-interfaces';
import { PageHandle } from './PageHandle';

export class PagesNamespace extends PrivateAPINamespace {
  static readonly namespace = 'pages';

  async list(): Promise<IPage[]> {
    if (!this.appDefinitionId) {
      throw new Error('appDefinitionId is not bound');
    }
    return this.ps.ds.pages
      .getPagesList()
      .filter((ref) => {
        const { managingAppDefId, appDefinitionId } = this.ps.ds.pages.data.get(
          ref.id,
        );
        return (managingAppDefId ?? appDefinitionId) === this.appDefinitionId;
      })
      .map((ref) => {
        const editorPointer =
          this.ps.platformPackagesBridge.EditorPointerAPI?.buildEditorPointer(
            ref,
          );
        if (!editorPointer) {
          return null;
        }
        return new PageHandle(this.ps, editorPointer).asIPage();
      })
      .filter(Boolean) as IPage[];
  }

  async addTemplate(
    templateId: string,
    options: AddTemplateOptions,
  ): Promise<IPage> {
    if (!this.appDefinitionId) {
      throw new Error('appDefinitionId is not bound');
    }
    const editorPointerAPI = this.ps.platformPackagesBridge.EditorPointerAPI;
    if (!editorPointerAPI) {
      throw new Error('EditorPointerAPI is not available');
    }

    const pageRef = this.ps.ds.pages.addPageFromPreset({
      presetName: templateId,
      uri: options.slug,
      title: options.title,
      hideFromMenu: !(options.addToSiteMenu ?? false),
    });
    await this.waitForChangesApplied();
    return new PageHandle(
      this.ps,
      editorPointerAPI.buildEditorPointer(pageRef),
    ).asIPage();
  }

  async addExtension(
    _extensionId: string,
    _options?: AddExtensionOptions,
  ): Promise<IPage> {
    // TODO: [EP-8700] Implement using addPageExtension once the addPage flow (blank page +
    //  content widgets) is extracted from addUnifiedComponents.ts for reuse between
    //  host-integration and the private SDK.
    throw new Error('pages.addExtension is not yet implemented');
  }
}
